/**
 * Idempotency-Key + retry helper for mutations.
 *
 * Why: a network blip mid-dispatch can cause the api-server to
 * either silently re-create the task on retry or fail the user's
 * submit without explanation. We attach a v4 UUID per mutation
 * attempt so the api-server can dedupe retries server-side.
 *
 * If the api-server doesn't honour `Idempotency-Key`, the header
 * is forwarded but ignored — no harm. When the server is
 * upgraded, the SPA already sends it.
 *
 * We also retry on `TypeError` (network) and HTTP `408/429/5xx`
 * with exponential backoff (200 ms → 800 ms → 3.2 s) so flaky
 * upstream behaviour doesn't show a generic "Dispatch failed"
 * toast when a retry would have succeeded.
 */

export interface RetryOptions {
  /** Total attempts (default 3 = original + 2 retries). */
  maxAttempts?: number;
  /** Base delay in ms for the first retry (default 200). */
  baseDelayMs?: number;
  /** Cap on backoff so the operator doesn't wait forever. */
  maxDelayMs?: number;
  /** Hook for the UI to show "Retrying (n/m)…" spinners. */
  onRetry?: (attempt: number, nextDelayMs: number, error: Error) => void;
  /** External abort signal (e.g. route change). */
  signal?: AbortSignal;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY = 200;
const DEFAULT_MAX_DELAY = 5_000;

/**
 * Decide whether `err` is worth retrying. Network errors (TypeError)
 * and HTTP `408`, `429`, `502`, `503`, `504` are transient. Other
 * 4xx responses (bad request, unauthorized, etc.) are not.
 */
export function shouldRetry(err: unknown): boolean {
  // Also true for `ApiNetworkError`, which SUBCLASSES `TypeError`
  // precisely so this predicate keeps holding for the rejections
  // `apiFetch` now wraps (v1-client.ts). Do not narrow this to
  // `TypeError` exactly.
  if (err instanceof TypeError) return true; // fetch network error
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const status = (err as { status: number }).status;
    return status === 408 || status === 429 || (status >= 500 && status <= 599);
  }
  return false;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * Run `fn` with retry + idempotency-key headers. Each attempt
 * generates a fresh UUID; if the api-server uses the key to dedupe,
 * the server only creates the resource once across all retries.
 */
export async function withRetry<T>(
  fn: (idempotencyKey: string, signal: AbortSignal) => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const baseDelay = opts.baseDelayMs ?? DEFAULT_BASE_DELAY;
  const maxDelay = opts.maxDelayMs ?? DEFAULT_MAX_DELAY;
  const ac = new AbortController();
  // Forward the parent signal into our internal one.
  if (opts.signal) {
    if (opts.signal.aborted) ac.abort();
    else opts.signal.addEventListener('abort', () => ac.abort(), { once: true });
  }

  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (ac.signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const idempotencyKey = crypto.randomUUID();
    try {
      return await fn(idempotencyKey, ac.signal);
    } catch (err) {
      lastErr = err;
      if (ac.signal.aborted) throw err;
      if (attempt === maxAttempts || !shouldRetry(err)) {
        throw err;
      }
      // Exponential backoff with full jitter to avoid thundering-herd.
      const expo = Math.min(maxDelay, baseDelay * Math.pow(4, attempt - 1));
      const delay = Math.floor(Math.random() * expo);
      opts.onRetry?.(attempt, delay, err as Error);
      await sleep(delay, ac.signal);
    }
  }
  throw lastErr;
}

/**
 * Wrapper for the most common case: a `fetch`-style call. Returns
 * the headers (including `Idempotency-Key`) that should be merged
 * into a `fetch()` call. Use with `withRetry` for the full retry
 * behaviour.
 */
export function idempotencyHeaders(key: string): HeadersInit {
  // `X-Idempotency-Key`, NOT the bare lowercase `idempotency-key`:
  // the api-server's CORS `allow_headers` lists the X- name (and
  // `v1-client.ts` already sends it), and an unlisted request header
  // makes the cross-origin preflight fail with 400 before the request
  // ever reaches a route — surfacing as `TypeError: Failed to fetch`
  // with nothing in the server log. Header NAMES are case-insensitive
  // on the wire, but the CORS allowlist match is against the name
  // itself, so `idempotency-key` and `X-Idempotency-Key` are two
  // different entries there.
  return {
    'X-Idempotency-Key': key,
  };
}
